import { useState, useEffect } from "react";
import { MessagingLastMessageProps, MessagingMessage, MessagingUser } from "./types";
import SendbirdChat, { type SendbirdChatWith } from '@sendbird/chat';
import { GroupChannelModule } from '@sendbird/chat/groupChannel';
import { OpenChannelModule } from '@sendbird/chat/openChannel';
import { AdminMessage, FileMessage, UserMessage } from "@sendbird/chat/message";

export const useLastMessage = ({ appKey, userId, channelUrl, regularUpdate, setUnreadStatus }: MessagingLastMessageProps) => {
    const [lastMessage, setLastMessage] = useState<MessagingMessage | null>(null);
    const [messageSender, setMessageSender] = useState<MessagingUser | null>(null);
    const [isUnread, setIsUnread] = useState<boolean>(false);

    useEffect(() => {
        let sb: SendbirdChatWith<(GroupChannelModule | OpenChannelModule)[]>;

        const getLastMessage = async () => {
            try {
                sb = SendbirdChat.init({
                    appId: appKey,
                    localCacheEnabled: true,
                    modules: [new GroupChannelModule(), new OpenChannelModule()]
                });

                await sb.connect(userId);

                const channel = await sb.groupChannel.getChannel(channelUrl);

                setIsUnread(channel?.unreadMessageCount > 0);
                setUnreadStatus(channel?.unreadMessageCount > 0);

                const lastChannelMessage = channel?.lastMessage;

                if(!!lastChannelMessage) {
                    if (lastChannelMessage instanceof FileMessage) {

                        setLastMessage({
                            type: lastChannelMessage.type.startsWith('image/') ? 'image' : 'file',
                            message: lastChannelMessage.name,
                            url: lastChannelMessage.url
                        });
                    } else if (lastChannelMessage instanceof UserMessage || lastChannelMessage instanceof AdminMessage){
                        setLastMessage({
                            type: 'text',
                            message: lastChannelMessage.message,
                            url: null
                        });
                    }

                    if(lastChannelMessage instanceof UserMessage || lastChannelMessage instanceof FileMessage) {
                        setMessageSender({
                            userId: lastChannelMessage.sender?.userId,
                            nickname: lastChannelMessage.sender?.userId === userId ? 'You' : lastChannelMessage.sender?.nickname,
                            profileUrl: lastChannelMessage.sender?.plainProfileUrl,
                            isCurrentUser: lastChannelMessage.sender?.userId === userId
                        });
                    }
                    else if(lastChannelMessage instanceof AdminMessage) {
                        setMessageSender({
                            userId: '',
                            nickname: 'Admin',
                            profileUrl: '',
                            isCurrentUser: false
                        });
                    }
                }
            }
            catch (err) {
                console.log(`Failed to get last message from sendbird: ${err}`)
            }
        };

        getLastMessage();

        if (regularUpdate) {
            const interval = setInterval(() => {
                getLastMessage();
            }, 60000); // update every 60 seconds

            return (() => {
                clearInterval(interval);
                if (!!sb) {
                    sb.disconnect();
                }
            });
        }
        else {
            return (() => {
                if (!!sb) {
                    sb.disconnect();
                }
            });
        }
    }, []);

    return { 
        lastMessage, 
        messageSender, 
        isUnread 
    };
}
/**
 * This hook is used to lock the body scroll when a touch event is detected on the given ref.
 * Prefent mobile scroll latching for the body during scrolling child elements.
 * @param ref - The ref to the element to lock the body scroll on.
 */
export function useLockBodyScrollOnTouch(ref: React.RefObject<HTMLElement>) {
    useEffect(() => {
        if (!ref.current) return;

        let unlockBody: (() => void) | null = null;

        const lockBodyScroll = () => {
            if (unlockBody) return unlockBody;
            const orig = {
                overflow: document.body.style.overflow,
            };
            document.body.style.overflow = 'hidden';
            unlockBody = () => {
                document.body.style.overflow = orig.overflow;
                unlockBody = null;
            };
            return unlockBody;
        };

        const start = (e: TouchEvent | PointerEvent) => {
            lockBodyScroll();
        };
        const end = () => {
            unlockBody && unlockBody();
        };

        const el = ref.current;
        el.addEventListener('touchstart', start as EventListener, { passive: false, capture: true });
        el.addEventListener('pointerdown', start as EventListener, { passive: false, capture: true });
        el.addEventListener('touchend', end, { capture: true });
        el.addEventListener('pointerup', end, { capture: true });
        el.addEventListener('touchcancel', end, { capture: true });

        return () => {
            el.removeEventListener('touchstart', start as EventListener, { capture: true } as any);
            el.removeEventListener('pointerdown', start as EventListener, { capture: true } as any);
            el.removeEventListener('touchend', end, { capture: true });
            el.removeEventListener('pointerup', end, { capture: true });
            el.removeEventListener('touchcancel', end, { capture: true });
            unlockBody && unlockBody();
        };
    }, [ref]);
}